import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export interface TicketSuporte {
  id: string;
  user_id: string;
  subject: string;
  description: string;
  category: 'tecnico' | 'pagamento' | 'conta' | 'servico' | 'reclamacao' | 'sugestao' | 'outro';
  priority: 'baixa' | 'media' | 'alta' | 'critica';
  status: 'aberto' | 'em_analise' | 'resolvido' | 'fechado';
  assigned_to?: string;
  assigned_to_name?: string;
  response_time_hours?: number;
  satisfaction_rating?: number;
  tags?: string[];
  created_at: string;
  updated_at: string;
  resolved_at?: string;
}

interface NovoTicket {
  subject: string;
  description: string;
  category: TicketSuporte['category'];
  priority?: TicketSuporte['priority'];
}

export const useTicketsSuporte = () => {
  const [tickets, setTickets] = useState<TicketSuporte[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const buscarTickets = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets((data || []) as TicketSuporte[]);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'tente novamente';
      toast.error('Erro ao carregar tickets: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  const criarTicket = async (ticket: NovoTicket) => {
    if (!user) {
      toast.error('Utilizador não autenticado');
      return false;
    }

    try {
      // Inserção direta no Supabase (a app de clientes não tem acesso ao backend admin).
      const { error } = await supabase.from('support_tickets').insert({
        user_id: user.id,
        category: ticket.category,
        priority: ticket.priority || 'media',
        subject: ticket.subject,
        description: ticket.description,
        status: 'aberto',
        tags: [],
      });

      if (error) throw error;

      toast.success('Recebemos o teu pedido. A nossa equipa entrará em contacto brevemente.');
      await buscarTickets();
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'tente novamente';
      toast.error('Erro ao enviar pedido: ' + msg);
      return false;
    }
  };

  const getTicketsRecentes = (limite: number = 3) => {
    return tickets.slice(0, limite);
  };

  useEffect(() => {
    if (user) {
      buscarTickets();
    }
  }, [user]);  

  return {
    tickets,
    loading,
    criarTicket,
    getTicketsRecentes,
    refetch: buscarTickets
  };
};